import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert } from "@fg/icons";
import { connectParityApi, type Meeting, type MeetingKind } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { MeetingRoom } from "./MeetingRoom";
import { PreJoin, type PreJoinChoice } from "./PreJoin";
import { breakoutParentOf } from "@/lib/breakout";

/**
 * Signed-in meeting route: /meeting/:meetingId.
 *
 * The meeting id belongs in the URL. The first cut of the deep link opened the
 * meeting from a "?join=" parameter and then stripped it, which meant the id
 * vanished from the address bar: you couldn't tell which meeting you were in,
 * couldn't share it, and a refresh dropped you out of the call entirely.
 *
 * Calls use the same route now, with two modes that skip the pre-join screen
 * the way Teams does:
 *   ?call=1            — you are placing the call; the room shows "Calling…".
 *   ?answer=video|audio — you accepted an incoming call with or without video.
 * Scheduled meetings go through the pre-join screen as before.
 */
export function MeetingPage() {
    const { meetingId } = useParams<{ meetingId: string }>();
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAccessToken();

    const placing = params.get("call") === "1";
    const answering = params.get("answer");
    // Moving between the main meeting and its breakout rooms skips the pre-join screen.
    const moving = params.get("bo") === "1";
    const calleeLabelFromState = (location.state as { calleeLabel?: string } | null)?.calleeLabel;

    const [subject, setSubject] = useState("Meeting");
    const [kind, setKind] = useState<MeetingKind>("video");
    const [meeting, setMeeting] = useState<Meeting | null>(null);
    const [callees, setCallees] = useState<Array<{ email: string; displayName?: string | null }>>([]);
    const [status, setStatus] = useState<"loading" | "ready" | "unavailable">("loading");
    const [choice, setChoice] = useState<PreJoinChoice | null>(() =>
        placing || answering || moving ? { micOn: true, camOn: answering ? answering === "video" : true } : null);

    useEffect(() => {
        if (!meetingId) { setStatus("unavailable"); return; }
        if (!token) return;
        let cancelled = false;
        void Promise.all([
            connectParityApi.publicMeeting(meetingId),
            connectParityApi.getMeeting(meetingId, token).catch(() => null)
        ])
            .then(([pub, full]) => {
                if (cancelled) return;
                setSubject(pub.subject);
                setKind(full?.kind ?? pub.kind ?? "video");
                setMeeting(full);
                setStatus(pub.joinable ? "ready" : "unavailable");
                if (placing && full?.isCall) {
                    void connectParityApi.meetingInvitees(meetingId, token)
                        .then((rows) => {
                            if (!cancelled) setCallees(rows.map((r) => ({ email: r.email, displayName: r.displayName })));
                        })
                        .catch(() => undefined);
                }
            })
            .catch(() => { if (!cancelled) setStatus("unavailable"); });
        return () => { cancelled = true; };
    }, [meetingId, token, placing]);

    if (status === "loading") {
        return (
            <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-zinc-950 text-white">
                <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                <p className="text-sm text-white/60">Loading meeting…</p>
            </div>
        );
    }

    if (status === "unavailable" || !meetingId) {
        return (
            <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-zinc-950 p-6 text-white">
                <ShieldAlert className="h-10 w-10 text-amber-400" />
                <h1 className="text-lg font-semibold">Meeting unavailable</h1>
                <p className="max-w-sm text-center text-sm text-white/60">
                    This meeting link is invalid, or the meeting was cancelled or has ended.
                </p>
                <button
                    type="button"
                    onClick={() => navigate("/", { replace: true })}
                    className="mt-2 h-9 rounded-md border border-white/15 px-4 text-sm hover:bg-white/10"
                >
                    Back to Connect
                </button>
            </div>
        );
    }

    if (!choice) {
        return (
            <PreJoin
                subject={subject}
                audioOnly={kind === "audio"}
                onJoin={setChoice}
                onCancel={() => navigate("/", { replace: true })}
            />
        );
    }

    return (
        <MeetingRoomForPage
            meetingId={meetingId}
            subject={subject}
            kind={kind}
            meeting={meeting}
            placing={placing}
            callees={callees}
            calleeLabelFromState={calleeLabelFromState}
            choice={choice}
        />
    );
}

/**
 * The room itself, with a STABLE onLeave: an inline arrow was a new function on every MeetingPage render,
 * which rebuilt the room's whole hub-handler map each time.
 */
function MeetingRoomForPage({ meetingId, subject, kind, meeting, placing, callees, calleeLabelFromState, choice }: {
    meetingId: string;
    subject: string;
    kind: MeetingKind;
    meeting: Meeting | null;
    placing: boolean;
    callees: Array<{ email: string; displayName?: string | null }>;
    calleeLabelFromState?: string;
    choice: PreJoinChoice;
}) {
    const navigate = useNavigate();
    const token = useAccessToken();
    // Being in the room makes you a participant, which is what lets you read the meeting's chat (and breakout-room commands).
    useEffect(() => {
        if (token) void connectParityApi.joinMeeting(meetingId, token).catch(() => undefined);
    }, [meetingId, token]);
    const onLeave = useCallback(() => navigate("/", { replace: true }), [navigate]);
    const calleeLabel = calleeLabelFromState
        ?? (callees.length === 1 ? (callees[0]!.displayName || callees[0]!.email) : callees.length > 1 ? `${callees.length} people` : undefined);

    return (
        <MeetingRoom
            meetingId={meetingId}
            meetingSubject={subject}
            kind={kind}
            hostUserId={meeting?.hostUserId}
            breakoutParentId={breakoutParentOf(meeting?.description)}
            scheduledEnd={meeting && !meeting.isCall ? meeting.scheduledEnd : null}
            placingCall={placing && (meeting?.isCall ?? false)}
            calleeLabel={calleeLabel}
            calleeCount={callees.length}
            initialMicOn={choice.micOn}
            initialCamOn={choice.camOn}
            deviceIds={{ cameraId: choice.cameraId, micId: choice.micId }}
            speakerId={choice.speakerId}
            onLeave={onLeave}
        />
    );
}
